// Angular Imports
import {
    ChangeDetectorRef,
    Component,
    EventEmitter,
    inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
    ViewContainerRef,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { CommonModule } from "@angular/common";

// RxJS Imports
import { finalize, Subject, takeUntil } from "rxjs";

// API Imports
import { FileManagerService } from "#services/file-manager/file-manager.service";

// Types Imports
import {
    FileData,
    FileManager,
    FileManagerActionType,
    SelectedFileData,
} from "#models/file-manager";

// Components Imports
import { LoaderComponent } from "#components/loader/loader.component";

// Variables Imports
import { variables } from "#src/app/shared/variables/variables";

// Translation Imports
import { TranslatePipe } from "#pipes/i18n/translate.pipe";

// Ant Imports
import { NzImage, NzImageModule, NzImageService } from "ng-zorro-antd/image";
import { NzUploadFile, NzUploadModule } from "ng-zorro-antd/upload";
import { NzBreadCrumbModule } from "ng-zorro-antd/breadcrumb";
import { NzPaginationModule } from "ng-zorro-antd/pagination";
import { NzCollapseModule } from "ng-zorro-antd/collapse";
import { NzButtonModule } from "ng-zorro-antd/button";
import { NzModalModule } from "ng-zorro-antd/modal";
import { NzIconModule } from "ng-zorro-antd/icon";
import { NzCardModule } from "ng-zorro-antd/card";
import { NzListModule } from "ng-zorro-antd/list";

//const
export const imageExtensions = [".png", ".jpg", ".jpeg", ".gif", ".webp", ".svg"];

@Component({
    selector: "app-file-manager-modal",
    templateUrl: "./file-manager-modal.component.html",
    styleUrls: ["./file-manager-modal.component.scss"],
    imports: [
        NzModalModule,
        NzBreadCrumbModule,
        NzUploadModule,
        NzIconModule,
        NzCardModule,
        NzListModule,
        NzButtonModule,
        NzCollapseModule,
        NzPaginationModule,
        NzImageModule,
        FormsModule,
        CommonModule,
        LoaderComponent,
        TranslatePipe,
    ],
})
export class FileManagerModalComponent implements OnInit, OnDestroy {
    @Input() isVisible = false;
    @Input() isSelectable = true;
    @Input() nzAccept: string[] | undefined = undefined;
    @Input() type: FileManagerActionType = "images";
    @Input() rememberLastState = true;
    @Input() stateStorageKey?: string;

    @ViewChild("modalTemplate", { read: ViewContainerRef }) modalTemplate!: ViewContainerRef;

    isLoading = false;
    breadcrumbs: string[] = [];
    folders: string[] = [];
    files: FileData[] = [];
    perPage = 12;
    totalFiles = 0;
    currentPath = "/";
    currentPage = 1;
    fallback = variables.NO_IMAGE;

    @Output() fileSelected = new EventEmitter<SelectedFileData>();
    @Output() closeEvent = new EventEmitter<void>();

    private destroy$ = new Subject<void>();
    private fileManagerService = inject(FileManagerService);
    private cdr = inject(ChangeDetectorRef);
    private imageService = inject(NzImageService);

    ngOnInit(): void {
        this.restorePersistedState();
        this.loadFiles();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    async handleOpenCreateFolderDialog() {
        const { CreateFolderModalComponent } =
            await import("#components/create-folder-modal/create-folder-modal.component");
        const ref = this.modalTemplate.createComponent(CreateFolderModalComponent);
        ref.setInput("currentPath", this.currentPath);
        ref.setInput("type", this.type);
        ref.instance.submitEmitter.subscribe(() => {
            this.loadFiles();
        });
        ref.instance.show();
    }

    async handleOpenSearchDialog() {
        const { FileManagerSearchDialogComponent } =
            await import("#components/file-manager/file-manager-search-dialog/file-manager-search-dialog.component");
        const ref = this.modalTemplate.createComponent(FileManagerSearchDialogComponent);

        ref.setInput("currentPath", this.currentPath);
        ref.setInput("type", this.type);

        ref.instance.resultsEmitter.subscribe((result) => {
            if ("type" in result) {
                if (result.type === "folder") {
                    this.navigateToFolder(result.path);
                } else if (result.type === "file") {
                    this.handleFileSelection(result.file);
                }
            }
        });

        ref.instance.show();
    }

    handleOpenFileManagerModal(): void {
        this.isVisible = true;
    }

    handleCloseFileManagerModal(): void {
        this.isVisible = false;
        this.closeEvent.emit();
    }

    handleSetLoading(state: boolean): void {
        setTimeout(
            () => {
                this.isLoading = state;
                this.cdr.detectChanges();
            },
            state ? 0 : 100
        );
    }

    loadFiles(): void {
        this.handleSetLoading(true);

        this.fileManagerService
            .getFoldersAndFiles(
                {
                    pageIndex: this.currentPage,
                    pageSize: this.perPage,
                    path: this.currentPath,
                    type: this.type,
                },
                () => {
                    this.handleInvalidPersistedState();
                }
            )
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => this.handleSetLoading(false))
            )
            .subscribe((response) => {
                const fileManager: FileManager = response.data;

                this.breadcrumbs = fileManager.breadcrumbs;
                this.folders = fileManager.folders;
                this.files = fileManager.files.data;
                this.totalFiles = fileManager.files.meta.total;

                this.persistCurrentState();
            });
    }

    navigateToFolder(path: string): void {
        this.currentPath = path;
        this.currentPage = 1;
        this.persistCurrentState();
        this.loadFiles();
    }

    handleFileSelection(file: FileData): void {
        const path =
            this.currentPath === "/" ? file.filename : `${this.currentPath}/${file.filename}`;

        this.persistCurrentState();

        this.handleSetLoading(true);
        this.fileManagerService
            .getFile({ path, type: this.type })
            .pipe(finalize(() => this.handleSetLoading(false)))
            .subscribe(({ data }) => {
                const fullFileData: SelectedFileData = {
                    ...file,
                    ...data,
                    path,
                };

                this.fileSelected.emit(fullFileData);
                this.handleCloseFileManagerModal();
            });
    }

    handleImagePreview(image: NzImage, file: FileData): void {
        if (this.isImage(file)) {
            this.imageService.preview([image], {
                nzZoom: 0.9,
                nzScaleStep: 0.25,
            });
        } else {
            window.open(file.url, "_blank");
        }
    }

    handleBeforeUpload = (file: NzUploadFile): boolean => {
        this.handleFileUpload(file);
        return false;
    };

    handleFileUpload(file: NzUploadFile): void {
        this.handleSetLoading(true);
        this.fileManagerService
            .uploadFile(this.currentPath, file, this.type)
            .pipe(finalize(() => this.handleSetLoading(false)))
            .subscribe(() => this.loadFiles());
    }

    handleChangePageIndex(page: number): void {
        this.currentPage = page;
        this.persistCurrentState();
        this.loadFiles();
    }

    handleChangePageSize(size: number): void {
        this.perPage = size;
        this.currentPage = 1;
        this.persistCurrentState();
        this.loadFiles();
    }

    getExtension(name: string): string {
        return name.split(".").pop() || "";
    }

    getIsSelectable(file: FileData): boolean {
        const ext = this.getExtension(file.filename);
        const accept = !this.nzAccept || this.nzAccept.includes(`.${ext}`);
        const isImageValidation =
            this.type !== "images" || (this.type === "images" && this.isImage(file));

        return this.isSelectable && accept && isImageValidation;
    }

    isImage(file: FileData): boolean {
        const ext = this.getExtension(file.filename);

        return imageExtensions.includes(`.${ext}`);
    }

    private restorePersistedState(): void {
        if (!this.rememberLastState) {
            this.currentPath = "/";
            this.currentPage = 1;
            return;
        }

        const state = this.fileManagerService.getPersistedState(this.stateStorageKey);

        this.currentPath = state.path;
        this.currentPage = state.page;
    }

    private persistCurrentState(): void {
        if (!this.rememberLastState) {
            return;
        }

        this.fileManagerService.setPersistedState(
            {
                path: this.currentPath,
                page: this.currentPage,
            },
            this.stateStorageKey
        );
    }

    private handleInvalidPersistedState(): void {
        if (this.currentPath !== "/" || this.currentPage !== 1) {
            this.currentPath = "/";
            this.currentPage = 1;

            if (this.rememberLastState) {
                this.fileManagerService.setPersistedState(
                    {
                        path: "/",
                        page: 1,
                    },
                    this.stateStorageKey
                );
            }

            this.loadFiles();
            return;
        }

        this.handleCloseFileManagerModal();
    }
}
